/**
 * The period a screen is showing, and the nights it covers (#169).
 *
 * Every screen that reports on a span of trading had been inventing this for
 * itself — sales had its own `Period` and `windowOf`, menu engineering had
 * no period at all, and a week meant whatever each file decided. One shape
 * here, resolved on the venue's own clock, so "last week" is the same seven
 * nights wherever it is read and a report can name the window it used.
 *
 * Service dates throughout, never instants: the caller has already decided
 * which night a 01:30 sale belongs to (`time.ts`).
 */
import { addDays, monthBoundsOf, monthShift, weekOf, } from "./time.js";
import { serviceDate } from "./prep.js";
export const DEFAULT_PERIOD = { kind: "this-week" };
/** The buttons, in the order they are offered. `range` is handled apart. */
export const PERIOD_CHOICES = [
    { kind: "this-week", label: "This week" },
    { kind: "last-week", label: "Last week" },
    { kind: "this-month", label: "This month" },
    { kind: "last-month", label: "Last month" },
    { kind: "range", label: "Date range" },
];
const window = (b) => ({ from: b.start, to: b.end });
/**
 * The nights a period covers, on the venue's clock.
 *
 * A range given back to front is read in the order that makes sense rather
 * than returning nothing: somebody filling in two date boxes gets the second
 * one wrong often enough that refusing is the wrong answer.
 */
export function windowOf(period, clock, now = new Date()) {
    const today = serviceDate(now, { timeZone: clock });
    switch (period.kind) {
        case "this-week":
            return window(weekOf(today, clock));
        case "last-week":
            return window(weekOf(addDays(today, -7), clock));
        case "this-month":
            return window(monthBoundsOf(today));
        case "last-month":
            return window(monthShift(today, -1));
        case "range":
            return period.from <= period.to
                ? { from: period.from, to: period.to }
                : { from: period.to, to: period.from };
    }
}
/**
 * The periods a week or a month is read against (#169).
 *
 * Three months back for the trend, and the same span a year ago for the
 * season — a bar in Melbourne in January is not comparable to the same bar
 * in July, and only last year's January says whether this one is good.
 *
 * The year-ago window is returned whether or not anything was trading then.
 * Whoever reads it has the figures and can say "nothing to compare", which
 * is the honest answer and better than quietly dropping the comparison.
 */
export function comparisonWindows(period, clock, now = new Date()) {
    const today = serviceDate(now, { timeZone: clock });
    if (period.kind === "this-month" || period.kind === "last-month") {
        const base = period.kind === "this-month" ? 0 : -1;
        return {
            recent: [1, 2, 3].map((n) => window(monthShift(today, base - n))),
            yearAgo: window(monthShift(today, base - 12)),
        };
    }
    if (period.kind === "this-week" || period.kind === "last-week") {
        const anchor = period.kind === "this-week" ? today : addDays(today, -7);
        // Months, not weeks, and deliberately: a week against the three months
        // behind it is the trend, where a week against three weeks is noise.
        const recent = [1, 2, 3].map((n) => window(monthShift(anchor, -n)));
        // 364 days, not a calendar year: 52 whole weeks lands on the same
        // weekday, so the comparison is Monday-to-Sunday against
        // Monday-to-Sunday rather than drifting a day and a half.
        return { recent, yearAgo: window(weekOf(addDays(anchor, -364), clock)) };
    }
    return { recent: [], yearAgo: null };
}
/**
 * The nights before a single night that it is read against: the three before
 * it, and the same night the week before (#169).
 */
export function dailyComparison(serviceDay) {
    return {
        priorDays: [1, 2, 3].map((n) => addDays(serviceDay, -n)),
        sameDayLastWeek: addDays(serviceDay, -7),
    };
}
/** "This week", or the dates when it is a range. */
export function periodLabel(period, shown) {
    const named = PERIOD_CHOICES.find((c) => c.kind === period.kind);
    return period.kind === "range" ? `${shown.from} to ${shown.to}` : (named?.label ?? "");
}
//# sourceMappingURL=period.js.map